import { memo, useMemo } from 'react';

import { tailwindVariants } from '@air/utils-tailwind';

import { NoPreviewImage } from '~/components/UI/NoPreviewImage';
import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import {
  WORKSPACE_AGENT_PROMPT_CARD_IMAGE_COUNT,
  WORKSPACE_AGENT_PROMPT_CARD_IMAGE_STACK_POSITIONS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentImprovedNullState';
import { WORKSPACE_AGENT_PANEL_PROMPT_CARD_THUMBNAIL } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';

const workspaceAgentPromptCardImageStackTile = tailwindVariants({
  base: 'absolute h-28 w-20 overflow-hidden rounded-8 border ads:border-subtle border-gray-1 ads:bg-subtle bg-gray-3 shadow-s',
  variants: {
    position: {
      back: 'top-4 left-4',
      front: 'top-0 left-0',
      middle: 'top-2 left-2',
    },
  },
});

export type WorkspaceAgentPromptCardThumbnail = {
  ext?: string;
  thumbnailUrl?: string;
};

export type WorkspaceAgentPromptCardImageStackProps = {
  /** Front first. A tile with no thumbnail and no extension stays a gray placeholder. */
  thumbnails: readonly WorkspaceAgentPromptCardThumbnail[];
};

export type WorkspaceAgentPromptCardImageStackTileProps = {
  position: (typeof WORKSPACE_AGENT_PROMPT_CARD_IMAGE_STACK_POSITIONS)[number];
  thumbnail: WorkspaceAgentPromptCardThumbnail | undefined;
};

const WorkspaceAgentPromptCardImageStackTile = memo(
  ({ position, thumbnail }: WorkspaceAgentPromptCardImageStackTileProps) => {
    const thumbnailUrl = thumbnail?.thumbnailUrl;
    const ext = thumbnail?.ext;
    const media = useMemo(() => {
      if (thumbnailUrl) {
        return (
          <WorkspaceAgentPanelImage
            {...WORKSPACE_AGENT_PANEL_PROMPT_CARD_THUMBNAIL}
            alt=""
            className="size-full object-cover"
            data-testid="WORKSPACE_AGENT_PROMPT_CARD_IMAGE"
            src={thumbnailUrl}
          />
        );
      }

      if (ext) {
        return (
          <span className="size-full">
            <NoPreviewImage color="color" ext={ext} hideText size="medium" />
          </span>
        );
      }

      return null;
    }, [ext, thumbnailUrl]);

    return <div className={workspaceAgentPromptCardImageStackTile({ position })}>{media}</div>;
  },
);

WorkspaceAgentPromptCardImageStackTile.displayName = 'WorkspaceAgentPromptCardImageStackTile';

/** Decorative fanned thumbnails: the front tile up and left, the rest stepping down and right. */
export const WorkspaceAgentPromptCardImageStack = memo(({ thumbnails }: WorkspaceAgentPromptCardImageStackProps) => {
  return (
    <div className="relative h-32 w-24" data-testid="WORKSPACE_AGENT_PROMPT_CARD_IMAGE_STACK">
      {/* Back to front: later tiles paint over earlier ones, so the stack needs no z-index. */}
      {WORKSPACE_AGENT_PROMPT_CARD_IMAGE_STACK_POSITIONS.map((position, index) => (
        <WorkspaceAgentPromptCardImageStackTile
          key={position}
          position={position}
          thumbnail={thumbnails.at(WORKSPACE_AGENT_PROMPT_CARD_IMAGE_COUNT - 1 - index)}
        />
      ))}
    </div>
  );
});

WorkspaceAgentPromptCardImageStack.displayName = 'WorkspaceAgentPromptCardImageStack';
